<template>
  <div>
    <div v-if="errorMessage" class="text-white text-center p-4">
      <!-- <h5>Something went wrong.</h5> -->
       <h5>Certificate found in Kingsland System</h5>
      <p v-html="errorMessage"></p>
    </div>

    <table v-else class="table table-borderless w-auto mx-auto">
      <tr v-for="(item, code) in items" :key="code">
        <td class="text-white">
          <div class="loader-ellipsis" v-if="item.isValidating">
            <span></span>
            <span></span>
            <span></span>
            <span></span>
          </div>
          <div v-else>
            <div v-if="item.isValid" id="checkbox">✔</div>
            <div v-else>
              <div v-if="item.errorMessage" id="wrongBox">❌</div>
            </div>
          </div>
        </td>
        <td class="table-text">
          <b>{{ item.name }}</b> <br />
          <span class="small" v-if="item.errorMessage">{{ item.errorMessage }}<br /></span>
          <span class="small field-details">{{ item.data }}</span>
        </td>
      </tr>
    </table>
  </div>
</template>

<script>
import { Verifier } from "kbc-verifier";
import { dictionary as VerifierErrors } from "kbc-verifier/lib/cjs/common/errors";
import moment from "moment";

// backup keys (sean@kingsland.io)
// infura: '8f5ac67824ff4fecb708ba9b3a834eae',
// alchemy: '_g8XrEHPE4xCMEeDH8Bjji_0t1VfGgl_',
// ankr: 'e54948a22e7579ef5d361e372b3022393e9ceaafbb25d49b4d25d1848490dde1'

const verifier = new Verifier({
  network: process.env.VUE_APP_NETWORK,
  etherScanApiKey: 'PHGQ2WCSV14I8R5RFPNQM1N1RNVQUWRBVJ',
  infura: '7e19c1daff4c43c28c37f4e2c4e26b56',
  alchemy: 'Uq1V_Fes6B-6mySY2zTmOHoOKpD0A9P6',
  ankr: 'afa2658c2b94d8b444b264369a10ed1bdab5a7271b882aba2c3d5ffbec76396a',
});

export default {
  props: ["data"],
  name: "verification",
  data() {
    return {
      errorMessage: "",
      items: {
        payloadIntegrity: {
          name: "Payload Integrity",
          isValid: false,
          isValidating: false,
          errorMessage: "",
          data: "",
        },
        institution: {
          name: "Institution",
          isValid: false,
          isValidating: false,
          errorMessage: "",
          data: "",
        },
        issuers: {
          name: "Issuers",
          isValid: false,
          isValidating: false,
          errorMessage: "",
          data: "",
        },
        merkleHashState: {
          name: "Merkle Hash State",
          isValid: false,
          isValidating: false,
          errorMessage: "",
          data: "",
        },
        certificateState: {
          name: "Certificate State",
          isValid: false,
          isValidating: false,
          errorMessage: "",
          data: "",
        }
      }
    };
  },
  methods: {
    async startVerification() {
      try {
        const { items, data } = this;
        const certificate = data;
        const payloadHash = certificate.signature.payloadHash;
        const keys = Object.keys(items);

        // reset status
        this.errorMessage = "";
        for (let key of keys) {
          items[key].isValid = false;
          items[key].errorMessage = "";
        }

        // A. Certificate integrity validation
        items.payloadIntegrity.isValidating = true;
        const integrityValidation = await verifier.validateIntegrity(certificate);
        items.payloadIntegrity.isValidating = false;
        items.payloadIntegrity.isValid = integrityValidation.valid;
        items.payloadIntegrity.errorMessage = integrityValidation.error;
        items.payloadIntegrity.data = payloadHash;

        // B. Institution Contract Validation
        items.institution.isValidating = true;

        /**
         * Hardcoded correction for institution address on certificates 
         * having staging institution address in production.
         */
        if (certificate.payload['@institutionAddress'].toLowerCase() === '0x4a30ccb819e8102cce0fd9d1f7de1ba79ef1157f') {
          certificate.payload['@institutionAddress'] = '0xb3b7681369e0e1f0a53b75feafe1292e57926da5';
        }
        const institutionValidation = await verifier.validateInstitution(certificate);
        items.institution.isValidating = false;
        items.institution.isValid = institutionValidation.valid;
        items.institution.errorMessage = institutionValidation.error;
        // items.institution.data = (await verifier.getInstitutionData(certificate)).institutionName; //original
        
        const { institutionName } = await verifier.getInstitutionData(certificate); //new
        const customProvider = certificate && certificate.payload && certificate.payload["@template"] && 
        certificate.payload["@template"].customCertificateProvider;
        // Only apply the comparison if `customCertificateProvider` is not empty or undefined
        items.institution.data = customProvider 
        ? (customProvider === institutionName ? institutionName : "") 
        : institutionName;

        // C. Issuers Validation
        items.issuers.isValidating = true;
        const issuersValidation = await verifier.validateIssuers(certificate);
        items.issuers.isValidating = false;
        items.issuers.isValid = issuersValidation.valid;
        items.issuers.errorMessage = issuersValidation.error;
        const issuersInfo = await Promise.all(certificate.payload["@issuers"].map(issuer => verifier.getIssuerData(certificate, issuer)));
        items.issuers.data = issuersInfo.map(info => info && (`${info.address} (${info.fullIssuerName})`) || 'n/a').join("\n");

        // D. Merkle Hash State Validation
        items.merkleHashState.isValidating = true;
        const merkleValidation = await verifier.validateMerkle(certificate);
        items.merkleHashState.isValidating = false;
        items.merkleHashState.isValid = merkleValidation.valid;
        items.merkleHashState.errorMessage = merkleValidation.error;
        items.merkleHashState.data = certificate.signature.merkle && (certificate.signature.merkle.root || certificate.signature.merkle.leaf) || payloadHash;

        // E. Certificate Hash State Validation
        items.certificateState.isValidating = true;
        const stateValidation = await verifier.validateState(certificate);
        items.certificateState.isValidating = false;
        items.certificateState.isValid = stateValidation.valid;
        items.certificateState.errorMessage = stateValidation.error;
        items.certificateState.data = await this.getCertificateStateDetails(certificate, stateValidation);

        const valid = (() => {
          for (let key of keys) {
            if (!items[key].isValid)
              return false;
          }
          return true;
        })();
        this.$emit("finished", valid);
      }
      catch (e) {
        // this.errorMessage = e.message || "Unknown error";
        this.errorMessage = "Due to a system migration, blockchain verification is not available at the moment.<br /><br />" +
                    "You can still view and download your certificate.";
                    this.$emit("finished", true);
      }
    },
    async getCertificateStateDetails(certificate, item) {
      const certState = await verifier.getCertificateState(certificate);
      const formatDate = date => moment(date).format("MMMM DD, YYYY");
      if (item.valid) return `Issued: ${formatDate(certState.issuedAt)}`;
      if (item.error === VerifierErrors.CERTIFICATE_REVOKED) return `Revoked: ${formatDate(certState.revokeAt)}`;
      if (item.error === VerifierErrors.CERTIFICATE_EXPIRED) return `Expired: ${formatDate(certState.expiresOn)}`;
    }
  }
};
</script>

<style scoped>
.verify-item {
  display: flex;
}

#checkbox {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  border: 1px solid green;
  font-size: 20px;
  color: green;
  text-align: center;
}
#wrongBox {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  border: 1px solid red;
  color: red;
  font-size: 20px;
}

.table-text {
  text-align: left;
  padding-top: 16px;
  color: white;
}
#icon-badge {
  height: 100px;
  width: 100px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50px;
  position: unset;
}

.verify-item .status-icon {
  /* padding-right: 0.2em; */
}

.field-details {
  white-space: pre;
}
</style>
